import { useMemo } from "react"
import ApiError from "../../../components/ApiError"
import StatisticsTable from "./StatisticsTable"
import { createColumnHelper } from "@tanstack/react-table"
import { useOrderResultsStatistics } from "../../../hooks/useOrderResultsStatistics"
import LinkButton from "../../../components/LinkButton"
import { useNavigate } from "react-router-dom"
import routePaths from "../../../constants/path"
import { DateTime } from "luxon"

const columnHelper = createColumnHelper()

export const OrderResultDetails = ({ filters, sorting, onSortingChange }) => {
  const navigate = useNavigate()

  const {
    data,
    isLoading: loading,
    error,
    isError,
  } = useOrderResultsStatistics({
    sortBy: sorting.map((s) => `${s.id}:${s.desc ? "DESC" : "ASC"}`).join(","),
    to: filters.to,
    from: filters.from,
    patientIssuer: filters.patientIssuer,
    status: filters.status,
  })

  const columns = useMemo(
    () => [
      columnHelper.accessor("PatientIssuer", {
        header: "Patient Issuer",
        id: "PatientIssuer",
        cell: (info) => (
          <LinkButton
            onClick={() => {
              const fromISO = DateTime.fromJSDate(
                new Date(filters.from)
              ).toISODate()
              const toISO = DateTime.fromJSDate(
                new Date(filters.to)
              ).toISODate()
              const patientIssuer = encodeURIComponent(info.renderValue())

              navigate(
                `${routePaths.results}?patientIssuer=${patientIssuer}&from=${fromISO}&to=${toISO}`
              )
            }}
          >
            {info.renderValue() || ""}
          </LinkButton>
        ),
        enableColumnFilter: false,
      }),
      columnHelper.accessor("PRELIMINARY", {
        header: "Preliminary",
        id: "PRELIMINARY",
        cell: (info) => info.renderValue(),
        enableColumnFilter: false,
        enableMultiSort: false,
        enableSorting: false,
      }),
      columnHelper.accessor("FINAL", {
        header: "Final",
        id: "FINAL",
        cell: (info) => info.renderValue(),
        enableColumnFilter: false,
        enableMultiSort: false,
        enableSorting: false,
      }),
      columnHelper.accessor("ADDENDUM", {
        header: "Addendum",
        id: "ADDENDUM",
        cell: (info) => info.renderValue(),
        enableColumnFilter: false,
        enableMultiSort: false,
        enableSorting: false,
      }),
      columnHelper.accessor("Error", {
        header: "Error",
        id: "Error",
        cell: (info) => info.renderValue(),
        enableColumnFilter: false,
        enableMultiSort: false,
        enableSorting: false,
      }),
      columnHelper.accessor("DELIVERED", {
        header: "Sent to Client Mirth",
        id: "DELIVERED",
        cell: (info) => info.renderValue(),
        enableColumnFilter: false,
        enableMultiSort: false,
        enableSorting: false,
      }),
      columnHelper.accessor("PatientDelivered", {
        header: "Sent to Customer",
        id: "PatientDelivered",
        cell: (info) => info.renderValue(),
        enableColumnFilter: false,
        enableMultiSort: false,
        enableSorting: false,
      }),
    ],
    [filters.from, filters.to, navigate]
  )

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <>
      <div className="bg-gray-100 border-t border-l border-r">
        <div className="px-3 py-6 font-bold text-xl text-start w-full bg-primary text-white">
          Results Statistics
        </div>
      </div>

      <StatisticsTable
        columns={columns}
        data={data?.data || []}
        loading={loading}
        showTotalBar={false}
        sorting={sorting}
        totalCount={data?.total || 0}
        onSortingChange={onSortingChange}
      />
    </>
  )
}

export default OrderResultDetails
